import React from "react";
import {
  CompanySettings,
  lockReason as reasonFor,
} from "../../domain/entities/CompanySettings";
import { SettingsFieldConfig } from "../config/companyDetailsForm";
import { DateFieldRow } from "./fields/DateFieldRow";
import { SecretFieldRow } from "./fields/SecretFieldRow";
import { SelectFieldRow } from "./fields/SelectFieldRow";
import { TextAreaFieldRow } from "./fields/TextAreaFieldRow";
import { TextFieldRow } from "./fields/TextFieldRow";
import { ToggleFieldRow } from "./fields/ToggleFieldRow";
import { FieldRowProps } from "./fields/fieldTypes";

const ROW_BY_KIND: Record<SettingsFieldConfig["kind"], React.FC<FieldRowProps>> = {
  text: TextFieldRow,
  select: SelectFieldRow,
  textarea: TextAreaFieldRow,
  secret: SecretFieldRow,
  // ZOUR-19.
  date: DateFieldRow,
  toggle: ToggleFieldRow,
};

interface SettingsFieldProps {
  config: SettingsFieldConfig;
  settings: CompanySettings;
  value: string;
  errorMessage: string | null;
  onChange: (field: string, value: string) => void;
}

/**
 * Picks the control for a field and feeds it the server's answer on whether it
 * is editable.
 *
 * Locking is never decided here: `settings.locked` comes from the API, which is
 * also what enforces it on write. The UI cannot accidentally offer an edit the
 * server would silently discard.
 */
export const SettingsField: React.FC<SettingsFieldProps> = ({
  config,
  settings,
  value,
  errorMessage,
  onChange,
}) => {
  const Row = ROW_BY_KIND[config.kind];
  const reason = reasonFor(settings, config.name);
  const options = config.optionsKey ? settings.options[config.optionsKey] ?? [] : [];

  // ZOUR-19. Resolved here rather than in the layout so the config stays
  // declarative: the field says which source it accepts a suggestion from, and
  // the only source so far is Zoho's own GST registration date. Absent when
  // Zoho was not read, was unreachable, or simply has no date on file — which
  // is common, so the button has to be able to not appear.
  const suggestion =
    config.suggestionKey === "zoho_registered_on"
      ? settings.zoho?.registeredOn ?? null
      : null;

  // A write-only field has no value to show, so the server's flag is the only
  // thing that can tell the user whether one is already stored.
  const statusNote =
    config.kind === "secret" && settings.secretSet !== undefined
      ? settings.secretSet
        ? "set"
        : "unset"
      : null;

  return (
    <Row
      config={config}
      value={value}
      // `locked` and `lockReason` are deliberately independent: a field can be
      // read-only WITHOUT the page explaining why every time. The server is
      // still the thing enforcing it.
      locked={reason !== null}
      lockReason={config.hideLockReason ? null : reason}
      errorMessage={errorMessage}
      options={options}
      statusNote={statusNote}
      suggestion={suggestion}
      suggestionLabel={config.suggestionLabel ?? null}
      onChange={(next) => onChange(config.name, next)}
    />
  );
};
